import cx from 'classnames';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import styled from 'styled-components';

export type TagSize = 'large' | 'normal' | 'small';

interface Props {
  /** Tag text. */
  children?: ReactNode;
  /** Leading Font Awesome icon class (e.g. `far fa-circle`). */
  icon?: string;
  /** Icon font-size, independent of the text (defaults to the text size). */
  iconSize?: number | string;
  /** Size preset. */
  size?: TagSize;
  /** Extra classes — pass a `cc-*` color class to override the default neutral. */
  className?: string;
  style?: CSSProperties;
}

const PADDING: Record<TagSize, string> = {
  small: '3px 8px',
  normal: '4px 10px',
  large: '6px 11px'
};

const FONT_SIZE: Record<TagSize, string> = {
  small: '10px',
  normal: '10px',
  large: '11px'
};

const Chip = styled.span<{ $size: TagSize }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  padding: ${({ $size }): string => PADDING[$size]};
  font-size: ${({ $size }): string => FONT_SIZE[$size]};
`;

/*
 * Reusable tag/badge. Defaults to the neutral color scheme; pass another
 * `cc-*` color class via `className` to recolor it.
 */
export const Tag = ({
  children,
  icon,
  iconSize,
  size = 'normal',
  className,
  style
}: Props): ReactElement => (
  <Chip $size={size} style={style} className={cx('cc-neutral', className)}>
    {icon !== undefined && (
      <i className={icon} style={iconSize === undefined ? undefined : { fontSize: iconSize }} />
    )}
    {children}
  </Chip>
);
